import PartnerTemplate from '../components/marketing/PartnerTemplate';

/* ============================================================
   Bankers' Bank of Kansas × Clarid — partnership page.

   AUDIENCE: a bankers' bank. Their customers ARE community
   banks ("We work exclusively for banks throughout our
   community"), so this is a channel/correspondent argument,
   not a single-institution sale. The page is written to be
   read by BBOK and then forwarded to a member bank.

   EVERYTHING ABOUT BBOK COMES FROM BBOK.COM. Their tagline
   ("Backing the banks building our communities"), their
   self-description ("We are community bankers"), their 1988
   founding, and their service list are their published
   language. Two earlier partner pages (Alvarez & Marsal,
   Duane Morris) shipped factual errors about the partner by
   inferring; nothing is inferred here.

   ⚠ THE ONE TRAP ON THIS PAGE: BBOK ALREADY SELLS
   "Independent Loan Review" as a top-level service line, and
   their own copy says their "tenured staff works exclusively
   on credit related services" — underwriting, loan
   administration, policy, acquisition due diligence. That is
   CREDIT review. Clarid's roadmap loan review is COMPLIANCE
   review. Presenting ours as new capability to a firm that
   has sold theirs since well before us would be both wrong
   and insulting, so the page names their line first, marks
   the distinction explicitly, and positions ours as the
   adjacent discipline rather than a replacement.

   EDIT-CHECK COUNT VERIFIED, not copied from another page:
   the registry holds 171 edits — validity 90, quality 51,
   macro-quality 23, syntactical 7. That is what "170+"
   rests on, and it is why the four categories are named in
   that order of weight rather than alphabetically.

   ROADMAP DISCIPLINE: Call Report *data* is ingested today
   from the FDIC BankFind API and drives peer benchmarking —
   that is shipped. Call Report *preparation* is roadmap. To a
   bankers' bank whose members file quarterly, blurring those
   two would read as a promise we cannot keep this year.
   ============================================================ */

export default function BBOKPartnerPage() {
  return (
    <PartnerTemplate
      docTitle="Bankers' Bank of Kansas + Clarid — Compliance Infrastructure for the Banks You Back"
      lockup="dual"
      partnerName="Bankers' Bank of Kansas"
      partnerLogo="/bbok-logo.png"
      eyebrow="§ PARTNERSHIP · BANKERS' BANK OF KANSAS × CLARID"
      headline={
        <>
          Backing the banks building our communities —{' '}
          <span className="frame-quote">on the compliance side too.</span>
        </>
      }
      lead="Since 1988, Bankers' Bank of Kansas has worked exclusively for banks — correspondent lending, payments, investments, and an independent loan review practice staffed by people who work only on credit. Clarid is the same idea one discipline over: HMDA, fair lending, and CRA analysis, built by a community-bank practitioner and validated by former FDIC and Federal Reserve examiners. A community bank cannot staff a fair-lending quant any more than it can staff a full credit review team. BBOK already solved the second problem for its members."
      heroNote={
        <>
          Prepared for Bankers' Bank of Kansas. Roadmap items are marked as roadmap throughout —
          nothing on this page is described as shipped unless it is running in production today.
        </>
      }
      thesisKicker="§ 01 · WHY THIS PARTNERSHIP"
      thesisHeadline="You already solve this problem. This is the same problem, one discipline over."
      thesis={[
        {
          well: '01',
          title: 'The correspondent argument, applied to compliance',
          body: "A bankers' bank exists because there are capabilities a community bank genuinely cannot build alone — and should not have to. Compliance analytics is now one of them. Fair lending regression, CRA assessment-area construction, and HMDA validation all require specialist depth that does not fit a bank with a two-person compliance department.",
        },
        {
          well: '02',
          title: 'Credit review has an exact compliance twin',
          body: "Your independent loan review gives a member bank rigorous credit work it could not staff internally, delivered by people who do only that. Fair lending, HMDA, and CRA are the same shape of problem on the regulatory side — and the same answer works. Clarid is the compliance-side engine, not a second opinion on credit.",
        },
        {
          well: '03',
          title: 'One engine, every bank you back',
          body: 'The analysis runs the same way for a $200M bank as for a $2B one, so extending it across a membership does not mean extending headcount proportionally. The marginal cost of the tenth member bank is not ten times the first.',
        },
      ]}
      bringsHeadline="What Clarid brings to the banks you back."
      depth={[
        {
          label: 'HMDA & LAR validation',
          txt: '170+ edit checks across validity, quality, macro-quality, and syntactical — each finding explained in plain language rather than an edit code. ML-assisted column mapping pulls the file straight out of the LOS, with geocoding and data-quality scoring on arrival.',
        },
        {
          label: 'Fair lending',
          txt: 'Underwriting, pricing, and redlining analysis with Census tract mapping and demographic overlays; denial-rate and pricing-disparity testing; logistic and OLS regression; and matched-pair file review. The consumer trial-balance file extends the same screens past HMDA to installment and consumer lending.',
        },
        {
          label: 'CRA & assessment areas',
          txt: 'Assessment-area construction resolved to the correct OMB delineation for each analysis year, geographic and borrower distribution, small business and community development analysis, and peer benchmarking drawn from FDIC Call Report data.',
        },
        {
          label: 'Exam & board packaging',
          txt: 'Findings across marketing, HMDA, fair lending, and CRA assemble into a single exam-ready package and a board-ready summary — cross-referenced, timestamped, and hash-verified, so the member bank hands a regulator a record rather than a folder.',
        },
      ]}
      proofs={[
        {
          stat: 'One graph, not five reports',
          desc: 'Marketing, HMDA, fair lending, redlining, regression, and CRA run as one connected record rather than six disconnected tools — so a finding in one surfaces in the exam package without anyone reconciling spreadsheets by hand.',
        },
        {
          stat: 'Deterministic by construction',
          desc: 'AI performs detection; the compliance determination is a deterministic rules engine. The figure in the draft is the figure on the call three weeks later, because nothing in between is a model deciding again.',
        },
        {
          stat: 'Every figure resolves to a source',
          desc: 'A tract income level traces to the Census record it came from; a qualification traces to the CFR section it rests on, under the institution’s own primary regulator. Immutable audit trail and SHA-256-verified exports.',
        },
      ]}
      integration={{
        kicker: '§ 03 · HOW IT WORKS TOGETHER',
        headline: 'The relationship is yours. The engine is ours.',
        lead: "BBOK already holds the trust relationship with the member bank and already delivers work those banks cannot staff. Clarid supplies the compliance analytics underneath, so the member bank experiences it as one more thing their bankers' bank does well.",
        barLabel: 'bbok.relationship → clarid.engine → member bank, exam-ready',
        flow: [
          { name: "BBOK · bankers' bank", sub: 'Correspondent relationship · credit review · exam & audit packets' },
          { name: 'Clarid · compliance engine', sub: 'HMDA · fair lending · CRA · exam packaging', clarid: true },
          { name: 'Member bank', sub: 'Examiner-ready, without adding compliance headcount' },
        ],
        tags: [
          { label: '✓ HMDA & LAR validation · shipped', kind: 'shipped' },
          { label: '✓ Fair lending & redlining · shipped', kind: 'shipped' },
          { label: '✓ CRA & assessment areas · shipped', kind: 'shipped' },
          { label: '✓ Exam & board packaging · shipped', kind: 'shipped' },
          { label: '✓ Peer benchmarking on Call Report data · shipped', kind: 'shipped' },
          { label: '◇ Compliance loan review · roadmap', kind: 'roadmap' },
          { label: '◇ BSA/AML program governance · roadmap', kind: 'roadmap' },
          { label: '◇ Call Report preparation · roadmap', kind: 'roadmap' },
          { label: '◇ Channel mechanics · to be scoped with BBOK', kind: 'roadmap' },
        ],
        note: "▢ Three distinctions worth stating plainly. BBOK's Independent Loan Review is a credit discipline — underwriting, loan administration, policy, acquisition due diligence — delivered by staff who work exclusively on credit; Clarid's roadmap loan review is the compliance counterpart and is not a substitute for it. Call Report data is ingested today and drives peer benchmarking; Call Report preparation is roadmap. BSA/AML is program governance — obligation register, risk assessment, policy hygiene — and is a layer above a monitoring engine, not a replacement for one. Integration shape is illustrative; commercial and channel mechanics to be scoped together.",
      }}
      contact={{ name: 'Bobby Sloan', title: 'Founder & CEO, Clarid', email: 'bobby@clarid.ai' }}
    />
  );
}
